import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Upload, FileSpreadsheet, Code, Webhook, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function IntegrationsPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [apiKey, setApiKey] = useState('');

  React.useEffect(() => {
    if (user?.user_role !== 'Super Administrator' && user?.user_role !== 'Manager') {
      navigate('/');
      return;
    }
    // Get API key (in production, this would be generated per user)
    const token = localStorage.getItem('token');
    setApiKey(token);
  }, [user]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(fileType)) {
        toast.error('Please upload Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleBulkUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    setUploadResults(null);

    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/leads/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploadResults(response.data);
      toast.success(`Successfully uploaded ${response.data.success_count} leads`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload leads');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `customer_name,mobile,email,projects,source,sub_source
John Doe,9876543210,john@example.com,"Aarambh,Solace",Facebook,Meta Campaign 1
Jane Smith,9876543211,jane@example.com,Vistara,Website,Organic Search`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead_upload_template.csv';
    a.click();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="integrations-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-button">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope' }}>Integrations & Upload</h1>
              <p className="text-sm text-slate-600">Bulk upload leads and connect external platforms</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4">
        <Tabs defaultValue="bulk-upload">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="bulk-upload" data-testid="bulk-upload-tab">
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Bulk Upload
            </TabsTrigger>
            <TabsTrigger value="meta-ads" data-testid="meta-ads-tab">
              <Webhook className="mr-2 h-4 w-4" />
              Meta Ads
            </TabsTrigger>
            <TabsTrigger value="api" data-testid="api-tab">
              <Code className="mr-2 h-4 w-4" />
              API & Automation
            </TabsTrigger>
          </TabsList>

          {/* Bulk Upload Tab */}
          <TabsContent value="bulk-upload">
            <Card data-testid="bulk-upload-card">
              <CardHeader>
                <CardTitle>Upload Leads from Excel/CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium">Required Columns:</p>
                      <ul className="text-sm space-y-1 ml-4 list-disc">
                        <li><strong>customer_name</strong> - Full name (required)</li>
                        <li><strong>mobile</strong> - Mobile number (required, unique, 7-15 digits)</li>
                        <li><strong>email</strong> - Email address (optional)</li>
                        <li><strong>projects</strong> - Comma-separated project names (required)</li>
                        <li><strong>source</strong> - Lead source (required: Magicbricks, 99 Acres, Facebook, etc.)</li>
                        <li><strong>sub_source</strong> - Additional source info (optional)</li>
                      </ul>
                    </div>
                  </AlertDescription>
                </Alert>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button">
                    <Download className="mr-2 h-4 w-4" />
                    Download Template
                  </Button>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="file-upload">Select File</Label>
                  <Input
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    data-testid="file-input"
                  />
                  {file && (
                    <p className="text-sm text-slate-600">Selected: {file.name}</p>
                  )}
                </div>

                <Button
                  onClick={handleBulkUpload}
                  disabled={!file || uploading}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="upload-button"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {uploading ? 'Uploading...' : 'Upload Leads'}
                </Button>

                {uploadResults && (
                  <Alert className={uploadResults.error_count > 0 ? 'border-amber-500' : 'border-green-500'}>
                    <AlertDescription>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="font-medium">Upload Complete!</span>
                        </div>
                        <div className="text-sm space-y-1">
                          <p>✅ Successfully created: {uploadResults.success_count} leads</p>
                          {uploadResults.error_count > 0 && (
                            <p className="text-amber-700">⚠️ Skipped/Failed: {uploadResults.error_count} leads</p>
                          )}
                        </div>
                        {uploadResults.errors && uploadResults.errors.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm font-medium">Errors:</p>
                            <ul className="text-xs text-slate-600 space-y-1 ml-4 list-disc max-h-40 overflow-y-auto">
                              {uploadResults.errors.slice(0, 10).map((err, idx) => (
                                <li key={idx}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meta Ads Tab */}
          <TabsContent value="meta-ads">
            <Card data-testid="meta-ads-card">
              <CardHeader>
                <CardTitle>Meta Lead Ads Integration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <AlertDescription>
                    Connect your Meta (Facebook/Instagram) Lead Ads directly to Real Estate CRM.
                    Leads will be automatically imported and assigned via round-robin.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Webhook URL</Label>
                    <div className="flex gap-2">
                      <Input
                        value={`${BACKEND_URL}/api/webhook/meta`}
                        readOnly
                        className="font-mono text-sm"
                        data-testid="webhook-url-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(`${BACKEND_URL}/api/webhook/meta`)}
                        data-testid="copy-webhook-button"
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                    <h3 className="font-semibold text-sm">Setup Instructions:</h3>
                    <ol className="text-sm space-y-2 list-decimal ml-5">
                      <li>Go to Meta Business Suite → Lead Ads</li>
                      <li>Select your Lead Ad form</li>
                      <li>Navigate to "Instant Forms" → "Settings"</li>
                      <li>Under "Lead Access", add a webhook</li>
                      <li>Paste the webhook URL above</li>
                      <li>Method: <code className="bg-white px-2 py-1 rounded">POST</code></li>
                      <li>Subscribe to "leadgen" events</li>
                    </ol>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Leads from Meta will be automatically assigned to brokers
                      based on available projects. Source will be set to "Facebook".
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API & Automation Tab */}
          <TabsContent value="api">
            <div className="space-y-4">
              {/* Make.com Card */}
              <Card data-testid="make-integration-card">
                <CardHeader>
                  <CardTitle>Make.com Integration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>API Endpoint</Label>
                    <div className="flex gap-2">
                      <Input
                        value={`${BACKEND_URL}/api/leads`}
                        readOnly
                        className="font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(`${BACKEND_URL}/api/leads`)}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Authorization Header</Label>
                    <div className="flex gap-2">
                      <Input
                        value={`Bearer ${apiKey}`}
                        readOnly
                        className="font-mono text-sm"
                        type="password"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(`Bearer ${apiKey}`)}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-sm mb-2">Make.com Setup:</h3>
                    <ol className="text-sm space-y-1 list-decimal ml-5">
                      <li>Create new scenario in Make.com</li>
                      <li>Add HTTP module → "Make a request"</li>
                      <li>URL: <code className="bg-white px-1 rounded">{`${BACKEND_URL}/api/leads`}</code></li>
                      <li>Method: POST</li>
                      <li>Headers: <code className="bg-white px-1 rounded">Authorization: Bearer [your-token]</code></li>
                      <li>Body type: JSON</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>

              {/* Zapier/n8n Card */}
              <Card data-testid="zapier-integration-card">
                <CardHeader>
                  <CardTitle>Zapier / n8n Integration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      Use the same API endpoint and authentication for Zapier and n8n workflows.
                    </AlertDescription>
                  </Alert>

                  <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                    <h3 className="font-semibold text-sm">Request Body (JSON):</h3>
                    <Textarea
                      rows={12}
                      readOnly
                      value={JSON.stringify({
                        customer_name: "John Doe",
                        mobile: "9876543210",
                        email: "john@example.com",
                        projects: ["Aarambh", "Solace"],
                        source: "Facebook",
                        sub_source: "Campaign Name"
                      }, null, 2)}
                      className="font-mono text-xs"
                    />
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-sm text-green-800 mb-2">Success Response (201):</h3>
                    <pre className="text-xs bg-white p-2 rounded overflow-x-auto">
{`{
  "id": "uuid",
  "lead_number": "L-0001",
  "customer_name": "John Doe",
  "mobile": "9876543210",
  "assigned_to": "broker-uuid",
  "assigned_to_name": "Amit Singh",
  "lead_status": "New"
}`}
                    </pre>
                  </div>
                </CardContent>
              </Card>

              {/* API Documentation Card */}
              <Card data-testid="api-docs-card">
                <CardHeader>
                  <CardTitle>API Documentation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-sm mb-2">Available Sources:</h3>
                      <div className="flex flex-wrap gap-2">
                        {['Magicbricks', '99 Acres', 'Facebook', 'Channel Partner', 'Website', 'Walkin', 'Housing.com', 'Reference'].map(source => (
                          <span key={source} className="bg-slate-100 px-2 py-1 rounded text-xs">{source}</span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-sm text-amber-800 mb-2">⚠️ Important Notes:</h3>
                      <ul className="text-xs text-amber-700 space-y-1 list-disc ml-5">
                        <li>Mobile numbers must be 7-15 digits and unique</li>
                        <li>At least one project must be specified</li>
                        <li>Duplicate mobile numbers will be rejected</li>
                        <li>Leads are auto-assigned via round-robin based on projects</li>
                        <li>Keep your API token secure and never share publicly</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default IntegrationsPage;
